import { Component, OnInit, ViewEncapsulation, ViewChild } from "@angular/core";
import { ProjectService } from "../../services/project/project.service";
import { Project } from "../../interfaces/project";
import { environment } from "../../../environments/environment";
import { ActivatedRoute } from "@angular/router";
import { Epic } from "../../interfaces/epic";
import { EditThemeDialogComponent } from "../edit-theme-dialog/edit-theme-dialog.component";
import { EpicService } from "../../services/epic/epic.service";
import { FeatureService } from "../../services/feature/feature.service";
import { DeleteItemConfirmationDialogComponent } from "../delete-item-confirmation-dialog/delete-item-confirmation-dialog.component";
import { MzToastService, MzModalService } from "ng2-materialize";
import { EditEpicDetailDialogComponent } from "../edit-epic-detail-dialog/edit-epic-detail-dialog.component";
import { Annotation } from "../../interfaces/annotation";
import { AnnotationService } from "../../services/annotation/annotation.service";
import { Annotationvariant } from "../../interfaces/annotationvariant";
import { ConfirmSyncComponent } from "../confirm-sync/confirm-sync.component";
import { JiraSyncProgressComponent } from "../jira-sync-progress/jira-sync-progress.component";

@Component({
  selector: "app-epics",
  templateUrl: "./epics.component.html",
  styleUrls: ["./epics.component.scss"],
  encapsulation: ViewEncapsulation.None,
})
export class EpicsComponent implements OnInit {
  env: any = environment;
  loading = true;
  project: Project;
  epics: Epic[];
  annotations: Array<Annotation>;

  @ViewChild("EditEpicModal") EditEpicModal: EditThemeDialogComponent;
  @ViewChild("EditEpicDetailModal")
  EditEpicDetailModal: EditEpicDetailDialogComponent;
  @ViewChild("DeleteConfirmationModal")
  DeleteConfirmationModal: DeleteItemConfirmationDialogComponent;
  @ViewChild("JiraSyncProgressModal")
  JiraSyncProgressModal: JiraSyncProgressComponent;

  constructor(
    private toastService: MzToastService,
    public modalService: MzModalService,
    private projectService: ProjectService,
    private epicService: EpicService,
    private annotationService: AnnotationService,
    private featureService: FeatureService,
    private route: ActivatedRoute
  ) {}

  ngOnInit() {
    /**
     * get all epics and feature by given projectID
     */
    this.route.params.subscribe(async (params: { id: string }) => {
      await this.loadData(params.id);
    });
  }

  /**
   * Event handler for floating action button for creating a new epic.
   */
  async openNewEpicDialog() {
    // this.EditEpicModal.modalComponent.open();
    this.epicClicked(this.epicService.getEmptyEpic(this.project._id));
  }

  async loadData(projectShortId?: string) {
    projectShortId = projectShortId || this.project.shortId;
    this.loading = true;
    this.project = await this.projectService.getProjectByShortId(
      projectShortId
    );
    this.EditEpicDetailModal.project = this.project;
    this.epics = (
      await this.epicService.getEpicsByProjectId(this.project._id)
    ).sort((x, y) =>
      x.name.localeCompare(y.name, undefined, {
        sensitivity: "base",
        ignorePunctuation: true,
        usage: "sort",
      })
    );
    await this.annotationService
      .getAnnotationsByProjectId(this.project._id)
      .then((annotations: Array<Annotation>) => {
        this.annotations = this.getSortedArray(annotations);
        //this.annotationvariants = this.flattenAnnotationVariants(this.annotations);
      });
    let features = await this.featureService.getFeaturesByProjectId(
      this.project._id
    );
    for (const epic of this.epics) {
      epic["itemCount"] = features.filter((x) => x.epicId === epic._id).length;
    }
    this.loading = false;
  }

  private getSortedArray(annotations) {
    return annotations.sort(
      (a: any, b: any) =>
        new Date(a.created_at).getTime() - new Date(b.created_at).getTime()
    );
  }

  flattenAnnotationVariants(
    annotations: Array<Annotation>
  ): Array<Annotationvariant> {
    return this.annotations.reduce((acc, annotation) => {
      return acc.concat(annotation.variants);
    }, []);
  }

  /**
   * Event handler for the output property of the EditThemeDialogComponent.
   * Uses the EpicService to create a new Epic.
   * @param args
   */
  async createEpicClicked(args) {
    console.log("Button clicked????");
    if (args.newTheme._id === undefined) {
      let createdTheme = await this.epicService.createEpic(args.newTheme);
      this.epics.push(createdTheme);
      this.epics = this.epics.sort((x, y) =>
        x.name.localeCompare(y.name, undefined, {
          sensitivity: "base",
          ignorePunctuation: true,
          usage: "sort",
        })
      );
      // await this.reloadProject();
      args.cbSubject.next(createdTheme);
      args.cbSubject.complete();
      await this.loadData();
    }
  }

  async deleteEpicClicked(id) {
    if (
      (await this.DeleteConfirmationModal.getResultAsync()) &&
      (await this.epicService.deleteEpicById(id))
    ) {
      // Load data again because links to this epic might have been updated
      await this.loadData();
      this.toastService.show("Epic gelöscht!", 4000, "notification-toast");
    }
  }

  /**
   * Event handler for clicking on an Epic card.
   */
  async epicClicked(epic) {
    // opens the modal dialog to edit the clicked epic
    // notice that the method will not return until the dialog is closed
    const resultTuple = await this.EditEpicDetailModal.getEditResultAsync(
      epic,
      this.epics,
      this.annotations
    );

    // result tuple contains two epic instances:
    // element at index 0 is the epic that has been edited in the modal dialog
    // element at index 1 is the linked epic that should be opened after closing the dialog

    if (resultTuple) {
      if (resultTuple[0]) {
        // if a new epic was created in this dialog (new epics don't have an id before they have been written to the DB)
        // we have to use a quick and dirty approach here, because createEpic is not able to process Epics with all attributes
        if (resultTuple[0]._id === undefined) {
          let simplifiedEpicForCreation: Epic = {
            color: resultTuple[0].color,
            name: resultTuple[0].name,
            projectId: resultTuple[0].projectId,
          } as Epic;
          let createdEpic: Epic = await this.epicService.createEpic(
            simplifiedEpicForCreation
          );

          resultTuple[0].agirId = createdEpic.agirId;
          resultTuple[0].created_at = createdEpic.created_at;
          resultTuple[0].shortId = createdEpic.shortId;
          resultTuple[0]._id = createdEpic._id;
        }

        await this.epicService.saveEpic(resultTuple[0]);
        this.toastService.show("Epic gespeichert!", 4000, "notification-toast");
      }
      await this.loadData();

      // If the user clicked on an epic link we need to open the corresponding epic
      if (resultTuple[1]) {
        // We need to replace the epic from the resultTuple with the fresh epic
        // because it might have been altered by adding dependencies
        const clickedEpic = this.epics.find((e) => e._id == resultTuple[1]._id);
        this.epicClicked(clickedEpic);
      }
    }
  }

  async syncProject() {
    const result = await (<ConfirmSyncComponent>(
      this.modalService.open(ConfirmSyncComponent).instance
    )).result
      .asObservable()
      .first()
      .toPromise();
    if (result === true) {
      await this.JiraSyncProgressModal.sync(this.project.shortId);
      await this.loadData();
    }
  }
}



// WEBPACK FOOTER //
// /agir/src/app/components/epics/epics.component.ts